/**
 * Returns true when a deal's season string falls in the "current or previous"
 * season window relative to a reference time.
 *
 * Season format: `"{YY}{variant}-{Suffix}"` (e.g. `"26A-Spr"`, `"25C-Fa"`).
 * Suffixes are `Win` / `Spr` / `Sum` / `Fa` and map to months as:
 *   - Winter   — Dec, Jan
 *   - Spring   — Feb–May
 *   - Summer   — Jun, Jul
 *   - Fall     — Aug–Nov
 *
 * "Relevant" matches:
 *   - the current (suffix, year) cell, OR
 *   - the previous suffix in the previous OR current year (covers the
 *     wrap-around from January, where "previous" is Fall of the prior year).
 *
 * `now` is injected so callers can test deterministically; production passes
 * `new Date()`.
 *
 * Returns false on malformed input (no dash, non-numeric year prefix).
 */
export function isRelevantSeason(dealSeason: string, now: Date): boolean {
	const month = now.getMonth(); // 0 = Jan, 11 = Dec
	const year2 = now.getFullYear() % 100;

	const seasonOrder = ["Win", "Spr", "Sum", "Fa"] as const;
	let currentIdx: number;
	if (month === 11 || month === 0) currentIdx = 0; // Winter
	else if (month <= 4) currentIdx = 1; // Spring
	else if (month <= 6) currentIdx = 2; // Summer
	else currentIdx = 3; // Fall

	const prevIdx = (currentIdx - 1 + 4) % 4;
	const currentSuffix = seasonOrder[currentIdx];
	const prevSuffix = seasonOrder[prevIdx];

	const dashIdx = dealSeason.lastIndexOf("-");
	if (dashIdx === -1) return false;
	const suffix = dealSeason.substring(dashIdx + 1);
	const dealYear = parseInt(dealSeason.substring(0, 2), 10);
	if (isNaN(dealYear)) return false;

	if (suffix === currentSuffix && dealYear === year2) return true;
	if (suffix === prevSuffix && (dealYear === year2 || dealYear === year2 - 1)) return true;
	return false;
}
